import { Suspense } from 'react';
import { Outlet, Route, Routes } from 'react-router-dom';

import { useSetAnalyticsGlobal } from '@cast/analytics';
import { DrawerProvider } from '@cast/design-system';
import { AccessControlProvider } from '@cast/infrastructure-abilities';

import { ProtectedRoute } from 'components/router';
import { SuspenseLoader } from 'components/loaders';
import {
  flattenNavItems,
  renderConfigRoutes,
  type OneClickLinkItem,
} from '@oneclick/routing';

import { SETTINGS_ROUTE_CONFIG } from './settingsRouteConfig';

const AccessControlRoutes = () => (
  <AccessControlProvider>
    <DrawerProvider>
      <Outlet />
    </DrawerProvider>
  </AccessControlProvider>
);

const settingsPages = flattenNavItems(SETTINGS_ROUTE_CONFIG);

const pageById = (id: string): OneClickLinkItem => {
  const page = settingsPages.find((p) => p.id === id);
  if (!page) {
    throw new Error(`Missing settings route config id: ${id}`);
  }
  return page;
};

const systemBridge = settingsPages.find((p) => p.route === '*')!;

export const SettingsController = () => {
  useSetAnalyticsGlobal('feature', 'Org. Settings');

  return (
    <Suspense fallback={<SuspenseLoader />}>
      <Routes>
        <Route element={<AccessControlRoutes />}>
          {renderConfigRoutes({
            pages: [pageById('dashboard')],
            suspense: false,
          })}
        </Route>
        <Route element={<ProtectedRoute subject="OrganizationBillingReport" />}>
          {renderConfigRoutes({
            pages: [
              pageById('platformUsageReportingPlans'),
              pageById('platformUsageReporting'),
              pageById('platformUsageDetail'),
              pageById('platformUsage'),
            ],
            suspense: false,
          })}
        </Route>
        <Route
          element={<ProtectedRoute subject="OrganizationClusterHibernate" />}
        >
          {renderConfigRoutes({
            pages: [pageById('clusterHibernation')],
            suspense: false,
          })}
        </Route>
        <Route
          element={<ProtectedRoute subject="OrganizationRebalanceSchedules" />}
        >
          {renderConfigRoutes({
            pages: [pageById('scheduledRebalancing')],
            suspense: false,
          })}
        </Route>
        <Route element={<ProtectedRoute subject="OrganizationCommitments" />}>
          {renderConfigRoutes({
            pages: [pageById('commitments')],
            suspense: false,
          })}
        </Route>
        {renderConfigRoutes({ pages: [systemBridge], suspense: false })}
      </Routes>
    </Suspense>
  );
};
